import { useEffect, useRef } from "react"
import { useCopilotChatInternal } from "@copilotkit/react-core"
import { useThreadContext } from "@/providers/ThreadProvider"
import { useCaseDraftStatus } from "@/features/oio/hooks/useCaseDraftStatus"
import { useOIOAgent } from "@/features/oio/hooks/useOIOAgent"
import { useGenerationStore } from "@/features/oio/generation.store"
import { useDraftReconnect } from "@/features/oio/hooks/useDraftReconnect"
import { useToast } from "@/hooks/use-toast"
import { isMockReplay } from "@/features/oio/mock/mockMode"
import { serviceForThread, type ServiceDescriptor } from "@/services/services.registry"

/**
 * Invisible, always-mounted (inside the hoisted CopilotKit provider) tracker
 * that mirrors the active case's generation status + progress into a global
 * store. Because the store lives outside the CopilotKit provider, it survives
 * the provider re-key on case switch and the reconnection gap — so returning to
 * a running case keeps its progress instead of bouncing to the upload page.
 * Also fires a notification + toast when a draft finishes.
 *
 * ORDER TYPE (checked first): only cases whose service descriptor says
 * `statusOwner: "shell"` (OIO) are tracked here. This component reads the OIO
 * co-agent and polls the shared run-status route, which speaks the OIO status
 * vocabulary and cannot tell an OIA draft from an OIA revise (design D3). For
 * an OIA case it would read the wrong agent's state and could write
 * `draft_not_started` over a running appeal. So it renders nothing, and the
 * OIA workspace owns its status (GET /api/v1/oia/cases/{id}) — see
 * services.registry `statusOwner`. The order-type check runs BEFORE the mock
 * check so the mock replay can never attach to an appeal.
 *
 * MOCK REPLAY (?mock=aditya): inert. This tracker pairs the REAL active
 * thread with useOIOAgent's state — but in mock mode that state comes from
 * the scripted replay, so mirroring it here would tag whatever real case is
 * open with mock progress, and useDraftReconnect would poll the backend for
 * it. The replay engine (mock/useMockRun.ts) maintains the generation store
 * for its own synthetic case instead.
 */
export function GenerationTracker() {
  const { activeThread } = useThreadContext()
  const service = serviceForThread(activeThread)
  if (service.statusOwner !== "shell") return null
  if (isMockReplay()) return null
  return <GenerationTrackerImpl service={service} />
}

function GenerationTrackerImpl({ service }: { service: ServiceDescriptor }) {
  const { activeThread } = useThreadContext()
  const draftStatus = useCaseDraftStatus()
  const { state: agent } = useOIOAgent()
  const { isLoading, messages } = useCopilotChatInternal()
  const setStatus = useGenerationStore((s) => s.setStatus)
  const setProgress = useGenerationStore((s) => s.setProgress)
  const heartbeat = useGenerationStore((s) => s.heartbeat)
  const { toast } = useToast()
  const prev = useRef<string | null>(null)

  // Catch up a draft that finished (or failed) while this tab was away — closed,
  // asleep, or disconnected by a backend redeploy. Polls authoritative metadata
  // when in flight with no live stream, then adopts the result + loads the blob.
  useDraftReconnect({ agentName: service.runsAgentName })

  const id = activeThread?.id ?? null
  const title = activeThread?.title ?? "Your case"
  const isRunning = draftStatus === "draft_starting" || draftStatus === "draft_running"

  // Ask for notification permission once a run is in flight.
  useEffect(() => {
    if (isRunning && typeof Notification !== "undefined" && Notification.permission === "default") {
      void Notification.requestPermission()
    }
  }, [isRunning])

  // Mirror live progress (completed stages / current stage) into the per-case store.
  useEffect(() => {
    if (!id || !isRunning) return
    setProgress(id, {
      completedStages: agent.completed_stages ?? [],
      currentStage: agent.current_stage ?? null,
      currentSubstage: agent.current_substage ?? null,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isRunning, agent.completed_stages, agent.current_stage, agent.current_substage, setProgress])

  // ── Stall heartbeat ──────────────────────────────────────────────────────
  // Refresh the heartbeat whenever we OBSERVE real activity for the active
  // running case (a checkpoint advance, a streamed message, the draft growing),
  // and reset a fresh grace window when (re)opening a running case — so the
  // backgrounded/just-reconnected case isn't wrongly flagged. If this goes
  // quiet while the case is still marked running, useStallDetection surfaces the
  // "looks stalled — reconnect/retry" affordance.
  const draftLen = agent.oio_draft?.length ?? 0
  const msgLen = messages?.length ?? 0
  useEffect(() => {
    if (!id || !isRunning) return
    heartbeat(id, Date.now())
  }, [id, isRunning, agent.completed_stages, agent.current_stage, agent.current_substage, draftLen, msgLen, heartbeat])

  // While CopilotKit is actively streaming this run, keep the heartbeat warm
  // even across a long single LLM call that emits nothing for a while.
  useEffect(() => {
    if (!id || !isRunning || !isLoading) return
    const t = setInterval(() => heartbeat(id, Date.now()), 4000)
    return () => clearInterval(t)
  }, [id, isRunning, isLoading, heartbeat])

  useEffect(() => {
    if (!id) return

    if (isRunning) {
      setStatus(id, "running", title)
    } else if (draftStatus === "draft_completed") {
      setStatus(id, "completed", title)
    } else if (draftStatus === "draft_failed_terminal") {
      setStatus(id, "failed", title)
    }

    // Fire a one-time notification + toast on the running → completed transition.
    if (prev.current && prev.current !== "draft_completed" && draftStatus === "draft_completed") {
      toast({
        title: "Order draft ready",
        description: `${title} — the ${service.badge} draft is ready for your review.`,
      })
      if (typeof Notification !== "undefined" && Notification.permission === "granted") {
        try {
          new Notification("Order draft ready", {
            body: `${title} — your ${service.badge} draft is ready for review.`,
          })
        } catch {
          // notifications can throw in some contexts — non-fatal
        }
      }
    }
    if (
      prev.current &&
      prev.current !== "draft_failed_terminal" &&
      draftStatus === "draft_failed_terminal"
    ) {
      toast({
        title: "Draft generation interrupted",
        description: `${title} — open Drafting to retry or report the issue.`,
        variant: "destructive",
      })
    }
    prev.current = draftStatus
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftStatus, id, isRunning])

  return null
}
